import React, { useEffect } from "react";
import StorefrontLayout from "../components/storefront/Layout";
import InquiryForm from "../components/storefront/InquiryForm";
import { useSeo } from "../lib/seo";
import { trackEvent } from "../lib/analytics";
import { BUSINESS, POSTAL_ADDRESS_LD } from "../lib/business";
import { Mail, MapPin, Truck, Phone, Clock } from "lucide-react";

const SUPPORT_EMAIL = BUSINESS.email;

export default function Contact() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useSeo({
    title: "Contact Lecis Auto Glass — Fitment Help for Exotic Auto Glass",
    description: "Need help finding the correct side mirror, windshield, or backglass? Contact Lecis Auto Glass and our team will verify fitment before you order. Fair Oaks, California — shipping nationwide.",
    canonicalPath: "/contact",
    jsonLd: {
      contactPage: {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "Contact Lecis Auto Glass",
        url: `${origin}/contact`,
      },
      organization: {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: BUSINESS.name,
        url: origin || "https://lecisautoglass.com",
        logo: `${origin}/logo.png`,
        email: BUSINESS.email,
        telephone: BUSINESS.phoneHref,
        address: POSTAL_ADDRESS_LD,
        contactPoint: [{
          "@type": "ContactPoint",
          telephone: BUSINESS.phoneHref,
          email: BUSINESS.email,
          contactType: "Customer Service",
          areaServed: "US",
          availableLanguage: "en",
        }],
      },
    },
  });

  useEffect(() => {
    trackEvent("contact_form_view", { page: "contact" });
  }, []);

  return (
    <StorefrontLayout>
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 md:py-24" data-testid="contact-page">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left: intro + info */}
          <div className="lg:col-span-5">
            <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-medium mb-5">Contact Lecis Auto Glass</h1>
            <p className="text-zinc-400 leading-relaxed mb-10">
              Need help finding the correct auto glass part? Our team can help verify fitment before you order. {BUSINESS.responseTime}
            </p>

            <div className="space-y-5">
              <a href={`tel:${BUSINESS.phoneHref}`} onClick={() => trackEvent("phone_click", { source: "contact_page" })} data-testid="contact-phone-link" className="flex items-center gap-4 group">
                <span className="w-11 h-11 rounded-md bg-white/5 border border-white/10 flex items-center justify-center text-red-500 group-hover:border-red-500/40 transition"><Phone size={18} /></span>
                <span>
                  <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500">Phone</span>
                  <span className="text-white group-hover:text-red-400 transition">{BUSINESS.phoneDisplay}</span>
                </span>
              </a>
              <a href={`mailto:${SUPPORT_EMAIL}`} onClick={() => trackEvent("email_click", { source: "contact_page" })} data-testid="contact-email-link" className="flex items-center gap-4 group">
                <span className="w-11 h-11 rounded-md bg-white/5 border border-white/10 flex items-center justify-center text-red-500 group-hover:border-red-500/40 transition"><Mail size={18} /></span>
                <span>
                  <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500">Support Email</span>
                  <span className="text-white group-hover:text-red-400 transition">{SUPPORT_EMAIL}</span>
                </span>
              </a>
              <a href={`mailto:${BUSINESS.infoEmail}`} onClick={() => trackEvent("email_click", { source: "contact_page_info" })} data-testid="contact-info-email-link" className="flex items-center gap-4 group">
                <span className="w-11 h-11 rounded-md bg-white/5 border border-white/10 flex items-center justify-center text-red-500 group-hover:border-red-500/40 transition"><Mail size={18} /></span>
                <span>
                  <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500">Information Email</span>
                  <span className="text-white group-hover:text-red-400 transition">{BUSINESS.infoEmail}</span>
                </span>
              </a>
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-md bg-white/5 border border-white/10 flex items-center justify-center text-red-500"><MapPin size={18} /></span>
                <span data-testid="contact-address">
                  <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500">Location</span>
                  <span className="text-white">{BUSINESS.address.line1}, {BUSINESS.address.city}, {BUSINESS.address.region} {BUSINESS.address.postalCode}</span>
                </span>
              </div>
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-md bg-white/5 border border-white/10 flex items-center justify-center text-red-500"><Clock size={18} /></span>
                <span data-testid="contact-hours">
                  <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500">Hours</span>
                  <span className="text-white">{BUSINESS.hoursDisplay}</span>
                </span>
              </div>
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-md bg-white/5 border border-white/10 flex items-center justify-center text-red-500"><Truck size={18} /></span>
                <span>
                  <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500">Shipping</span>
                  <span className="text-white">Nationwide</span>
                </span>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <div className="bg-[#0b0b0b] border border-white/10 rounded-lg p-6 md:p-8">
              <InquiryForm inquiryType="contact" />
            </div>
          </div>
        </div>
      </div>
    </StorefrontLayout>
  );
}
